<template>
  <Page subpage data-cy="feature-settings">
    <div class="flex justify-between items-center mb-4">
      <h3 class="page-header-2">Feature Settings</h3>
      <SlotWrapper>
        <template #header>
          <SeraButton
            data-cy="feature-settings-save"
            :disabled="saving"
            @click="handleSave"
          >
            <span v-if="!saving">Save</span>
            <Spinner v-else message="Saving..." />
          </SeraButton>
        </template>
      </SlotWrapper>
    </div>
    <div class="feature-settings-body">
      <FeatureSettingRow
        v-for="feature in features"
        v-bind="feature"
        :key="feature.key"
        v-model="settings[feature.key]"
      />
    </div>
  </Page>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { getProperty } from "@/lib/helpers";

import SeraButton from "@/components/Button.vue";
import SlotWrapper from "@/components/SlotWrapper";
import Page from "../components/Page.vue";
import Spinner from "../components/Spinner.vue";
import FeatureSettingRow from "./FeatureSettingRow.vue";

export default {
  components: {
    SlotWrapper,
    SeraButton,
    Page,
    Spinner,
    FeatureSettingRow
  },
  props: {
    featureSettings: {
      type: Object,
      default: null
    }
  },
  emits: ["update:settings"],
  data() {
    return {
      saving: false
    };
  },
  computed: {
    ...mapGetters(["featureEnabledForTenant"]),
    settings: {
      get() {
        return this.featureSettings;
      },
      set(val) {
        this.$emit("update:settings", val);
      }
    },
    features() {
      const features = [
        {
          key: "pre_work_enabled",
          testSelector: "preWorkEnabled",
          value: this.settings.pre_work_enabled,
          label: "Enable Appointment Pre-Work",
          description:
            "Enable the Pre-Work flow in the Tech App to begin capturing Pre-Work time in your Job Time Efficiency.",
          learnMoreLink: this.preWorkLearnMoreLink
        }
      ];
      if (this.featureEnabledForTenant("custom_arrival_windows_enabled")) {
        const arrivalWindowOptions = [];
        for (let i = 1; i < 11; i++) {
          arrivalWindowOptions.push({
            label: `${i} Hour${i > 1 ? "s" : ""}`,
            value: i * 3600
          });
        }
        features.push({
          key: "arrival_window_duration_seconds",
          testSelector: "arrivalWindowDurationSeconds",
          value: this.settings.arrival_window_duration_seconds,
          label: "Arrival Window Duration",
          description:
            "Changes the duration (length of time) shown for the arrival windows when scheduling an appointment.",
          learnMoreLink: this.arrivalWindowsLearnMoreLink,
          options: arrivalWindowOptions
        });
      }
      features.push({
        key: "time_cards_enabled",
        testSelector: "timeCardsEnabled",
        value: this.settings.time_cards_enabled,
        label: "Timesheets",
        description:
          "Allow users to capture clock-in and clock-out times in Sera with overtime tracking starting at 40 hours per workweek.",
        learnMoreLink: this.timeCardsLearnMoreLink
      });
      if (this.settings.time_cards_enabled) {
        features.push({
          label: "Enable Daily Overtime",
          indent: true,
          key: "daily_overtime_enabled",
          testSelector: "dailyOvertimeEnabled",
          value: this.settings.daily_overtime_enabled,
          description: "Enable overtime tracking per day."
        });
      }
      if (
        this.settings.time_cards_enabled &&
        this.settings.daily_overtime_enabled
      ) {
        features.push({
          indent: true,
          key: "daily_overtime_hours",
          inputType: "number",
          testSelector: "dailyOvertimeHours",
          value: this.settings.daily_overtime_hours,
          label: "Daily Overtime Hours",
          description:
            "The maximum number of working hours in a day before overtime rates start to apply."
        });
        features.push({
          label: "Enable Daily Double Overtime",
          indent: true,
          key: "daily_double_overtime_enabled",
          testSelector: "dailyDoubleOvertimeEnabled",
          value: this.settings.daily_double_overtime_enabled,
          description: "Enable daily double overtime tracking per day."
        });
        if (this.settings.daily_double_overtime_enabled) {
          features.push({
            indent: true,
            key: "daily_double_overtime_hours",
            inputType: "number",
            testSelector: "dailyDoubleOvertimeHours",
            value: this.settings.daily_double_overtime_hours,
            label: "Daily Double Overtime Hours",
            description:
              "The maximum number of working hours in a day before double overtime rates start to apply."
          });
        }
      }
      if (this.featureEnabledForTenant("job_status")) {
        features.push({
          key: "job_status_enabled",
          testSelector: "jobStatusEnabled",
          value: this.settings.job_status_enabled,
          label: "Job Status",
          description:
            "Allow admins to enter and manage the status of Jobs for easier tracking, visibility, and discovery.",
          learnMoreLink: this.jobStatusLearnMoreLink
        });
      }
      features.push({
        key: "full_day_schedule_enabled",
        testSelector: "fullDayScheduleEnabled",
        value: this.settings.full_day_schedule_enabled,
        label: "Full-Day Schedule",
        description:
          "Enable Full-Day Schedule so technicians can view all of their scheduled appointments for the day.",
        learnMoreLink: this.fullDayScheduleLearnMoreLink
      });
      features.push({
        label: "Separate Communication Opt-Out",
        key: "separate_communication_opt_out_enabled",
        testSelector: "separateCommunicationOptOutEnabled",
        value: this.settings.separate_communication_opt_out_enabled,
        description: "Enable separate communication opt-out for users.",
        learnMoreLink: this.separateCommunicationOptOutLearnMoreLink
      });
      return features;
    },
    preWorkLearnMoreLink: function () {
      const link = getProperty(
        this.$store.state.tenantCustomizations,
        "System.pre_work_learn_more_link"
      );
      return link?.value || link?.default;
    },
    arrivalWindowsLearnMoreLink: function () {
      const link = getProperty(
        this.$store.state.tenantCustomizations,
        "System.arrival_windows_learn_more_link"
      );
      return link?.value || link?.default;
    },
    timeCardsLearnMoreLink: function () {
      const link = getProperty(
        this.$store.state.tenantCustomizations,
        "System.time_cards_learn_more_link"
      );
      return link?.value || link?.default;
    },
    jobStatusLearnMoreLink: function () {
      const link = getProperty(
        this.$store.state.tenantCustomizations,
        "System.job_status_learn_more_link"
      );
      return link?.value || link?.default;
    },
    separateCommunicationOptOutLearnMoreLink: function () {
      const link = getProperty(
        this.$store.state.tenantCustomizations,
        "System.separate_communication_opt_out_learn_more_link"
      );
      return link?.value || link?.default;
    },
    fullDayScheduleLearnMoreLink: function () {
      const link = getProperty(
        this.$store.state.tenantCustomizations,
        "System.full_day_schedule_learn_more_link"
      );
      return link?.value || link?.default;
    }
  },
  watch: {
    "settings.daily_overtime_enabled"(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.settings.daily_overtime_hours = newValue ? 8 : 24;
        if (!newValue) {
          this.settings.daily_double_overtime_enabled = false;
        }
      }
    },
    "settings.daily_double_overtime_enabled"(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.settings.daily_double_overtime_hours = newValue ? 12 : 24;
      }
    }
  },
  methods: {
    ...mapActions(["fetchTenant"]),
    handleSave() {
      this.saving = true;
      const payload = this.settings;
      $.ajax(`/company/settings`, {
        method: "PUT",
        contentType: "application/json",
        dataType: "json",
        data: JSON.stringify({ settings: payload }),
        success: () => {
          this.saving = false;
          window.notify.success("Settings updated successfully!");
          // fetch tenant
          this.fetchTenant();
        },
        error: (e) => {
          this.saving = false;
          window.notify.error(
            (e.responseJSON || {}).message || "Unable to save settings"
          );
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.feature-setting {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}
</style>
